/**
 * Help content for both portals.
 *
 * Written from what the modules actually do, so the rules quoted here are the ones the API enforces
 * (a mandatory booking date range on Master Data, one pickup per merchant per day, a required LR/AWB
 * on a shipment ticket…). When a rule changes in code, change it here too — a help page that
 * confidently describes behaviour the system no longer has is worse than no help page.
 *
 * `audience` decides which portal shows a topic: 'internal' = staff panel only, 'merchant' = merchant
 * portal only, 'both' = shown in each (the wording is written to be true for both).
 */

export type Audience = 'internal' | 'merchant' | 'both';

export interface HelpTopic {
  /** Screen / module name, matching the sidebar label so it can be found by eye. */
  title: string;
  /** Where it lives in the menu, e.g. "Order Management › Orders". */
  path?: string;
  audience: Audience;
  /** One or two sentences: what this screen is for. */
  what: string;
  /** When you would come here. */
  when?: string;
  /** The main job, as numbered steps. */
  how?: string[];
  /** Things that catch people out — enforced rules, gotchas, irreversible actions. */
  notes?: string[];
}

export interface HelpSection {
  name: string;
  blurb?: string;
  topics: HelpTopic[];
}

export const HELP_SECTIONS: HelpSection[] = [
  {
    name: 'Getting started',
    blurb: 'How the panel is laid out and what drives what.',
    topics: [
      {
        title: 'How the panel fits together',
        audience: 'both',
        what: 'A shipment moves through the same chain every time: an order is created, a courier is chosen and a waybill is generated (an AWB for B2C, an LR for B2B), the courier scans it as it travels, and those scans drive everything downstream — tracking, NDR, weight disputes, freight, invoices and remittance.',
        when: 'Read this once before using the rest.',
        how: [
          'Create or import the order — it starts as Not Shipped with no waybill.',
          'Ship it: pick a courier and generate the waybill. The order now has an AWB (B2C) or LR (B2B).',
          'Courier scans arrive automatically by webhook and by a sweep every four hours; you do not have to refresh anything.',
          'A failed delivery raises an NDR. A heavier re-weigh raises a Weight discrepancy. Both feed the money side.',
          'Freight prices each shipment, invoices bill it, and remittance pays the merchant their COD.',
        ],
        notes: [
          'Almost everything you see is derived from the order and its scans — if a screen looks wrong, check the order first.',
          'B2C is tracked by AWB, B2B by LR. A B2B consignment usually has no AWB at all, which is normal.',
        ],
      },
      {
        title: 'Finding your way around',
        audience: 'internal',
        what: 'The sidebar groups screens by area. What you can see depends on your role — if a screen is missing, your role has not been given that module.',
        how: [
          'Use the sidebar search or scan the group headings (Order Management, Finance & Billing, Stakeholders & HR…).',
          'If a screen you need is missing, ask an administrator to grant it under Users → Roles & Permissions.',
        ],
        notes: ['Super Admin and Admin see everything; other roles see only what their role allows.'],
      },
    ],
  },

  {
    name: 'Orders & shipping',
    topics: [
      {
        title: 'Orders B2C',
        path: 'Order Management › Orders',
        audience: 'both',
        what: 'Every parcel shipment. Tabs split the list by where the order is: Not Shipped, Today Uploads, Booking Failed, In Transit, Delivered, RTO and so on.',
        when: 'Day-to-day booking, and whenever you need to find one parcel.',
        how: [
          'Open the Not Shipped tab to see orders waiting for a courier.',
          'Tick the orders you want and press Choose Courier & Ship, or open one order and ship it on its own.',
          'The AWB appears on the row once the courier accepts it. If it does not, the order moves to Booking Failed with the courier\'s reason.',
          'Use the search box for an AWB, order number or customer phone.',
        ],
        notes: [
          'Today Uploads means shipments BOOKED today that carry a waybill — not rows created today.',
          'A booking failure is not a lost order: fix the cause and ship it again from Not Shipped.',
        ],
      },
      {
        title: 'Orders B2B',
        path: 'Order Management › Orders',
        audience: 'both',
        what: 'Freight consignments, tracked by an LR number rather than an AWB, usually multi-box.',
        how: [
          'Create the order with its box list — each box\'s weight and dimensions matter, they drive the pricing.',
          'Ship it to generate the LR. Delhivery B2B may return a job id first and the LR a few minutes later; the panel fills it in automatically.',
          'Print labels once the box waybills exist.',
        ],
        notes: [
          'A B2B order shows an LR, and often no AWB. That is correct.',
          'If the LR has not appeared after a few minutes, check Booking Failed — the courier may have rejected it.',
        ],
      },
      {
        title: 'Orders Bucket',
        path: 'Order Management › Orders',
        audience: 'both',
        what: 'Orders that arrived from a sales channel or API but are not ready to ship — missing an address, a pincode we do not serve, or an incomplete product line.',
        how: ['Open a row, fix what is flagged, and push it through to the normal order list.'],
      },
      {
        title: 'Tracking',
        path: 'Order Management › Orders',
        audience: 'both',
        what: 'The scan history of a shipment, and where staff record a scan by hand when a courier has not sent one.',
        how: [
          'Search the AWB or LR.',
          'Read the event list, newest first.',
          'Internal staff can add a scan — pick the status, add remarks, and attach a POD for a delivery.',
        ],
        notes: [
          'Marking a shipment Delivered or RTO closes any open NDR for it automatically.',
          'A delivery scan with a POD settles the shipment; only an administrator can change it afterwards.',
        ],
      },
      {
        title: 'Pickup Requests',
        path: 'Order Management',
        audience: 'internal',
        what: 'The collection booked with the courier for a merchant\'s parcels.',
        how: [
          'Requests are raised automatically when a waybill is generated — one per merchant per day, covering everything they ship that day.',
          'Check the list to see whether the courier accepted it (Confirmed, with a pickup id) or rejected it (Failed, with the reason).',
          'Press Retry on a failed one once the cause is fixed.',
        ],
        notes: [
          'You cannot raise a second request for the same merchant on the same day — one pickup covers B2B and B2C together.',
          'A Delhivery B2B row may sit as Pending and say it was not sent: their B2B pickup route is not enabled for our account yet. It is sent automatically if a B2C shipment follows the same day.',
          'A pickup fails if the pickup location is not registered with that courier — register it under Pickup Locations first.',
        ],
      },
      {
        title: 'Appointments',
        path: 'Order Management › Orders',
        audience: 'internal',
        what: 'Delivery slots booked with a consignee who will only receive goods at an agreed time.',
      },
      {
        title: 'In-House OFD Orders',
        path: 'Order Management › Orders',
        audience: 'internal',
        what: 'Shipments out for delivery on our own fleet rather than a courier partner.',
      },
    ],
  },

  {
    name: 'Exceptions',
    blurb: 'Where shipments go when something does not go to plan.',
    topics: [
      {
        title: 'NDR',
        path: 'Order Management',
        audience: 'both',
        what: 'Non-Delivery Reports — a delivery was attempted and failed. The merchant is asked what to do: reattempt, change the address or phone, or return it.',
        when: 'Every working day; an NDR left unanswered becomes an RTO.',
        how: [
          'Open the NDR list and work the oldest first.',
          'Read the courier\'s reason on the row.',
          'Choose an action — Reattempt (optionally with a new address or phone) or Return.',
          'The courier is told, and the shipment carries on.',
        ],
        notes: [
          'An NDR closes by itself the moment the shipment is delivered or goes RTO — you will not see finished shipments in this queue.',
          'Changing the address here changes the delivery attempt, not the original order.',
        ],
      },
      {
        title: 'Weight',
        path: 'Order Management',
        audience: 'both',
        what: 'Weight discrepancies — the courier weighed a parcel heavier than we booked, so they charge more. Each one is a claim you can accept or dispute.',
        how: [
          'Open the list and compare booked weight against the courier\'s weight.',
          'Accept it if the courier is right — the shipment is re-priced at the new weight.',
          'Dispute it with proof (photos of the parcel on the scale, and its dimensions) if it is wrong.',
        ],
        notes: [
          'An undisputed discrepancy is accepted automatically after 48 hours, so raise a dispute inside that window.',
          'A shipment can only be charged for extra weight ONCE — if it has already been charged, accepting a second claim is refused.',
          'Accepting is a financial decision: it changes what the merchant is billed.',
        ],
      },
      {
        title: 'EDD Expired',
        path: 'Order Management',
        audience: 'internal',
        what: 'Shipments past their estimated delivery date that are still moving, so they can be chased before the customer complains.',
      },
      {
        title: 'IVR',
        path: 'Order Management',
        audience: 'internal',
        what: 'Automated calls to consignees on failed deliveries, and the recordings and outcomes that come back.',
      },
      {
        title: 'Bulk POD',
        path: 'Order Management',
        audience: 'internal',
        what: 'Upload proof-of-delivery images in bulk and attach them to shipments by AWB.',
        how: ['Name each file after its AWB, select them all, and upload — they are matched automatically.'],
      },
      {
        title: 'Support Tickets',
        audience: 'both',
        what: 'Issues raised against the platform or a shipment, with a conversation thread, an owner and an SLA.',
        how: [
          'Press New Ticket, choose the category, and describe the issue.',
          'For anything about a specific parcel, quote the LR or AWB — the category will insist on it.',
          'Replies go back and forth on the thread; internal notes stay hidden from the merchant.',
        ],
        notes: [
          'A CS Manager can select several tickets in the list and assign them to a CS agent in one action.',
          'A private ticket is internal only — the merchant never sees it or receives email about it.',
        ],
      },
    ],
  },

  {
    name: 'Money',
    topics: [
      {
        title: 'Freight',
        path: 'Finance & Billing',
        audience: 'both',
        what: 'What each shipment costs, worked out from the merchant\'s rate card: zone, weight slabs, COD fee, fuel, and any surcharges.',
        how: [
          'Find the shipment by AWB or LR.',
          'Open the row to see how the price was built up, charge by charge.',
          'Export the list for a period when you need to reconcile.',
        ],
        notes: [
          'The charged weight is what was actually billed, which can be higher than what was booked if a weight discrepancy was accepted.',
          'A returned shipment has two rows: the forward leg and the RTO leg.',
        ],
      },
      {
        title: 'Invoices',
        path: 'Finance & Billing',
        audience: 'both',
        what: 'Tax invoices raised against a merchant for a billing period.',
        how: [
          'Choose the merchant and the period, and generate — the freight for that window is totalled.',
          'Open the invoice to print it, or download it as Excel.',
          'The Excel has two sheets: the invoice itself, and the shipments behind the total.',
        ],
        notes: [
          'A manually raised invoice has no shipment breakdown — the figure was entered by hand.',
          'If the shipment sheet does not match the invoice total, the sheet says so: freight can be re-priced after an invoice is raised.',
        ],
      },
      {
        title: 'Credit & Debit Notes',
        path: 'Finance & Billing',
        audience: 'both',
        what: 'Adjustments against an invoice — a credit note reduces what a merchant owes, a debit note increases it.',
      },
      {
        title: 'Remittance',
        path: 'Finance & Billing',
        audience: 'both',
        what: 'COD money collected from customers on delivery and paid back to the merchant, in cycles.',
        how: [
          'Delivered COD shipments appear here automatically.',
          'Group them into a remittance cycle and mark it paid with the payment reference.',
        ],
        notes: ['A shipment only becomes remittable once it is actually delivered — an out-for-delivery parcel is not.'],
      },
      {
        title: 'Wallet & Ledger',
        path: 'Finance & Billing',
        audience: 'both',
        what: 'The running balance of a prepaid merchant, every charge and recharge against it, and the account statement.',
        notes: ['Freight is debited as it is computed, so the wallet moves as shipments are priced, not when the invoice is raised.'],
      },
      {
        title: 'Price Calculator',
        path: 'Finance & Billing',
        audience: 'both',
        what: 'Quote a shipment before booking it — enter the route, weight and dimensions and see what each courier would charge.',
        how: ['Enter pickup and delivery pincodes, weight and box size, then compare the rates offered.'],
      },
    ],
  },

  {
    name: 'Reporting',
    topics: [
      {
        title: 'Master Data',
        path: 'Order Management',
        audience: 'internal',
        what: 'The full shipment export — every order with its dates, weights, charges, courier and status, for reconciliation and MIS.',
        how: [
          'Set the booking date range (this is required).',
          'Add any other filters — merchant, courier, status, service.',
          'Check the record count, then Export, or Send Email to have the file mailed.',
        ],
        notes: [
          'A booking date range is mandatory, because without one the export would pull every order ever booked.',
          'The one exception is a lookup by AWB, LR, order number or invoice number — those are already specific, so no dates are needed.',
        ],
      },
      {
        title: 'Reports & Dashboard',
        audience: 'both',
        what: 'Volume, delivery performance, NDR and RTO rates, and courier comparison over a period.',
      },
    ],
  },

  {
    name: 'Warehouse (WMS)',
    blurb: 'Stock held in our warehouses on behalf of a merchant.',
    topics: [
      {
        title: 'Inbound — GRN, Put Away, QC',
        path: 'WMS › Inbound',
        audience: 'internal',
        what: 'Receiving stock: a GRN records what physically arrived, Put Away posts it onto the shelf as good or damaged, and QC re-checks what was put away.',
        how: [
          'Create a GRN against the merchant and supplier, listing what was received.',
          'Put it away, splitting the quantity into good and damaged.',
          'Run QC if the stock needs a second inspection; corrections are posted as a movement, never as a second receipt.',
        ],
      },
      {
        title: 'Inventory & Stock Transfer',
        path: 'WMS › Inventory',
        audience: 'both',
        what: 'What is on hand per product, per warehouse — available, reserved and damaged — and the ledger of every movement.',
        notes: ['Merchants see their own stock only.'],
      },
      {
        title: 'Outbound — Orders, Dispatch, Labels',
        path: 'WMS › Outbound',
        audience: 'internal',
        what: 'Picking, packing and dispatching orders from warehouse stock. Dispatching books the courier and deducts the stock.',
        notes: ['Stock is deducted once, at dispatch — re-dispatching will not deduct it twice.'],
      },
      {
        title: 'Returns — RTO Inward, QC, Restock',
        path: 'WMS › Returns',
        audience: 'internal',
        what: 'Goods coming back: received at the gate, inspected, then restocked as sellable or damaged.',
      },
      {
        title: 'Cycle Count',
        path: 'WMS › Inventory',
        audience: 'internal',
        what: 'Counting the shelf against the system and posting the difference as an adjustment.',
        how: ['Generate a count sheet, enter what was physically counted, then post it to apply the variance.'],
      },
      {
        title: 'WMS Reports',
        path: 'WMS › Reports',
        audience: 'internal',
        what: 'Stock levels, space utilisation, ageing, dispatch volume and the billing report that prices storage and handling.',
      },
    ],
  },

  {
    name: 'Network & couriers',
    topics: [
      {
        title: 'Pickup Locations',
        path: 'Network & Warehousing',
        audience: 'internal',
        what: 'The addresses parcels are collected from. Each one must be registered with a courier before that courier will collect from it.',
        how: [
          'Add the address with its pincode and contact.',
          'Register it with each courier that will collect there.',
        ],
        notes: ['An unregistered location is the usual reason a pickup request fails.'],
      },
      {
        title: 'Serviceability',
        path: 'Network & Warehousing',
        audience: 'internal',
        what: 'Which pincodes each courier serves, and for what — forward, reverse, COD, prepaid.',
      },
      {
        title: 'Couriers & Pricing',
        path: 'Courier Operations',
        audience: 'internal',
        what: 'The courier partners, the services they offer, and the rates we buy at.',
      },
      {
        title: 'Status Mapping & Webhooks',
        path: 'Courier Operations / System',
        audience: 'internal',
        what: 'How each courier\'s own scan codes translate into our statuses, and the endpoints they push scans to.',
        notes: ['An unmapped courier code leaves a shipment on its previous status — check here when tracking looks stuck.'],
      },
    ],
  },

  {
    name: 'People & access',
    topics: [
      {
        title: 'Merchants',
        path: 'Stakeholders & HR',
        audience: 'internal',
        what: 'The merchant master — profile, billing address, rate cards, pickup locations, couriers enabled, and the CS and sales owners.',
        how: [
          'Open a merchant and use the tabs for profile, pricing, couriers and warehouses.',
          'Pricing is per courier and drives every freight calculation for that merchant.',
        ],
      },
      {
        title: 'Users, Roles & Permissions',
        path: 'Stakeholders & HR',
        audience: 'internal',
        what: 'Staff logins and what each role can see and do, module by module and tab by tab.',
        how: [
          'Create the user and give them a role.',
          'Edit the role to tick view / add / edit / delete / export per module tab.',
        ],
        notes: ['A screen missing from someone\'s sidebar is almost always a missing role permission.'],
      },
      {
        title: 'HR',
        path: 'Stakeholders & HR',
        audience: 'internal',
        what: 'Employees, attendance, salary, PF and ESI, stationery stock, issued assets and repairs — plus Notifications for emailing staff.',
        how: [
          'Use Notifications to build a group of employees and send them a circular.',
          'Employees with no email on their HR record fall back to their login address; anyone with neither is reported as skipped.',
        ],
      },
      {
        title: 'Notifications',
        audience: 'internal',
        what: 'Email campaigns to merchants — build a group, write once with attachments, send, and watch the per-recipient log.',
        how: [
          'Create a group (filter by state, then tick the merchants).',
          'Compose the message, attach files if needed, and preview it.',
          'Send, then follow progress in the Send Log and retry any failures.',
        ],
        notes: ['Merchants with no valid email are logged as skipped rather than silently dropped.'],
      },
    ],
  },

  {
    name: 'Your account',
    topics: [
      {
        title: 'Settings',
        audience: 'merchant',
        what: 'Your company profile, pickup addresses, notification email addresses and account preferences.',
      },
      {
        title: 'API Keys & Sales Channels',
        audience: 'both',
        what: 'Connect your own systems: API keys for direct integration, and sales channels (such as Shopify) that pull orders in automatically.',
        how: [
          'Generate an API key and keep it safe — it is shown once.',
          'Or connect a sales channel and let orders arrive on their own.',
        ],
      },
      {
        title: 'Customers',
        audience: 'both',
        what: 'The consignees you have shipped to, with their addresses, so repeat orders can be created quickly.',
      },
    ],
  },
];
